import addDays from 'date-fns/addDays'
import en from 'date-fns/locale/en-US'
import sr from 'date-fns/locale/sr'
import srLatn from 'date-fns/locale/sr-Latn'
import { currentLocale } from 'i18n/translation'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { useFormikContext } from 'formik'

registerLocale('en-us', en)
registerLocale('sr-sl', srLatn)
registerLocale('sr-sc', sr)

const toLocalDate = value => {
  if (!value) return null
  if (value instanceof Date) return value
  const date = new Date(value)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

const formatForField = date => moment(date).format('YYYY-MM-DDTHH:mm:ss.000000[Z]')

const formatEndForField = date =>
  moment
    .utc(date, 'YYYY-MM-DDTHH:mm:ss.000000[Z]')
    .add(23, 'hours')
    .add(59, 'minutes')
    .add(59, 'seconds')
    .format('YYYY-MM-DDTHH:mm:ss.000000[Z]')

export const CustomDateRangePicker = ({
  startName,
  endName,
  startLabel,
  startPlaceholder,
  defaultStartValue = null,
  maxDays = null,
  align = 'left',
}) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  const startDate = toLocalDate(values[startName])
  const endDate = toLocalDate(values[endName])
  const today = new Date()
  const maxDate =
    maxDays != null && startDate && !endDate
      ? new Date(Math.min(addDays(startDate, maxDays - 1).getTime(), today.getTime()))
      : today

  const handleChange = ([start, end]) => {
    setFieldValue(startName, start ? formatForField(start) : null).then(() => setFieldTouched(startName))
    setFieldValue(endName, end ? formatEndForField(end) : null).then(() => setFieldTouched(endName))
    if (start && end) setOpen(false)
  }

  const handleClear = e => {
    e.stopPropagation()
    setFieldValue(startName, defaultStartValue ? formatForField(defaultStartValue) : null)
    setFieldValue(endName, null)
    setOpen(false)
  }

  useEffect(() => {
    const onClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const displayStart = startDate ? moment(startDate).format('DD.MM.YYYY') : ''
  const displayEnd = endDate ? moment(endDate).format('DD.MM.YYYY') : ''
  const displayValue = displayStart && displayEnd ? `${displayStart} – ${displayEnd}` : displayStart || displayEnd || ''
  const hasValue = startDate && endDate

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div className="mb-3">
        {startLabel && <label className="form-label">{startLabel}</label>}
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            readOnly
            value={displayValue}
            placeholder={startPlaceholder}
            onClick={() => setOpen(true)}
            style={{ cursor: 'pointer' }}
          />
          {hasValue ? (
            <span className="input-group-text" style={{ cursor: 'pointer' }} onClick={handleClear}>
              <i className="bi bi-x-lg" />
            </span>
          ) : (
            <span className="input-group-text" style={{ cursor: 'pointer' }} onClick={() => setOpen(true)}>
              <i className="bi bi-calendar3" />
            </span>
          )}
        </div>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 18px)',
            ...(align === 'right' ? { right: 0 } : { left: 0 }),
            zIndex: 1050,
            minWidth: '580px',
          }}
        >
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleChange}
            monthsShown={2}
            inline
            locale={currentLocale}
            maxDate={maxDate}
          />
        </div>
      )}
    </div>
  )
}
